import {
	ICampaignDefinition,
	IFloodgateAuthTokenCallback,
	IMessageSpec,
} from "@ms-ofb/floodgate-shared-typings";

import { AgeGroup, AuthenticationType, IBeaconParameters, IMessagingServiceSettings, IUserFact } from "../Common/Models";

import { ContentType, ContentTypeToStorageKeyMap } from "../Common/ContentType";
import {addClientIngestionTimeAndNormalizeKeys} from "../Facts/UserFactsClient";
import * as Caching from "../Utils/Caching";
import * as Storage from "../Utils/Storage";
import * as Transport from "../Utils/Transport";

import { buildQueryParameters, IQueryParams } from "../Utils/UrlUtils";

import * as Logging from "../Logging/Logger";
import * as TmsgConstants from "./TargetedMessagingConstants";

import { getDynamicSetting, SettingKey } from "../Configuration/DynamicConfiguration";

import { ContentResponse } from "../Common/ContentResponse";
import { EventIds, ICustomProperties } from "../Logging/Constants";
import { IAuthTokenConfig } from "@ms-ofb/floodgate-shared-typings/dist/src/Core/Api/IFloodgateAuthTokenCallback";
import { getUserFactList, getUserFacts } from "../Facts/UserFacts";
import { getSamplingRate } from "../Common/Settings";
import { mergeLocalUserFacts } from "../Facts/CachedUserFacts";
import { getErrorMessage } from "../Utils/Utils";

interface ITmsServiceResponse {
	MessageMetadata: IMessageSpec;
	UserGovernanceRules: any;
	CampaignContent: any;
	DynamicSettings: any;
	LogLevelSettings: ILogLevelSettings;
}

const enum LogLevel {
	None,
	Critical,
	Error,
	Info,
}

interface ILogLevelSettings {
	logLevel: LogLevel;
}

interface IDebugOverrides {
	addTmsQueryParams?: Record<string, string>;
	replaceTmsQueryParams?: Record<string, string>;
	tmsResponse?: ITmsServiceResponse;
	userFactsResponse?: ITmsServiceResponse;
}

export function getDebugCampaignDefinitions(): ContentResponse {
	const debugOverrides = Storage.readObjectFromStorage<IDebugOverrides>("obf-DebugOverrides", {});
	return debugOverrides && debugOverrides.tmsResponse && debugOverrides.tmsResponse.CampaignContent && {
		contentType: ContentType.campaignContent,
		content: debugOverrides.tmsResponse.CampaignContent,
	};
}

export function getDebugUserFacts(): ContentResponse {
	const debugOverrides = Storage.readObjectFromStorage<IDebugOverrides>("obf-DebugOverrides", {});
	return debugOverrides && debugOverrides.userFactsResponse && {
		contentType: ContentType.userFacts,
		content: debugOverrides.userFactsResponse,
	};
}

export class TargetedMessagingClient {
	private locale = "en-us";
	private country = "US";
	private authTokenCallback: IFloodgateAuthTokenCallback;
	private app: string;
	private platform: string;
	private version: string;
	private packageVersion: string;
	private sessionId: string;
	private campaignFlights: string;
	private campaignParameters: string;
	private ageGroup: AgeGroup;
	private authType: AuthenticationType;
	private campaignMetadataAggregatorPrefix: string;
	private setCampaignActionPrefix: string;
	private messagingServiceSettings: IMessagingServiceSettings;

	constructor(
		app: string,
		platform: string,
		version: string,
		locale: string,
		country: string,
		sessionId: string,
		authTokenCallback: IFloodgateAuthTokenCallback,
		campaignFlights?: string,
		campaignParameters?: string,
		ageGroup?: AgeGroup,
		authType?: AuthenticationType,
		audience?: string,
		messagingServiceSettings?: IMessagingServiceSettings,
		packageVersion?: string,
		) {
		this.locale = locale;
		this.country = country;
		this.authTokenCallback = authTokenCallback;
		this.app = app;
		this.platform = platform;
		this.version = version;
		this.packageVersion = packageVersion;
		this.sessionId = sessionId;
		this.campaignFlights = campaignFlights;
		this.campaignParameters = campaignParameters;
		this.ageGroup = ageGroup;
		this.authType = authType;
		this.messagingServiceSettings = messagingServiceSettings;

		const internalEnvs = ["dogfood", "edog", "int", "microsoft"];
		const isInternalEnv = audience && internalEnvs.indexOf(audience.toLowerCase()) !== -1;
		const campaignMetadataAggregatorConfig = isInternalEnv ? this.messagingServiceSettings?.campaignMetadataAggregatorInt
		: this.messagingServiceSettings?.campaignMetadataAggregatorProd;
		const setCampaignActionConfig = isInternalEnv ? this.messagingServiceSettings?.setCampaignActionInt
		: this.messagingServiceSettings?.setCampaignActionProd;

		const cmaDefaultBaseUrlPrefix = isInternalEnv ? TmsgConstants.TMS_CAMPAIGNMETADATAAGGREGATOR_PREFIX_EDOG
		: TmsgConstants.TMS_CAMPAIGNMETADATAAGGREGATOR_PREFIX_PROD;
		const scaDefaultBaseUrlPrefix = isInternalEnv ? TmsgConstants.TMS_SETCAMPAIGNACTION_PREFIX_EDOG
		: TmsgConstants.TMS_SETCAMPAIGNACTION_PREFIX_PROD;

		this.campaignMetadataAggregatorPrefix = campaignMetadataAggregatorConfig || cmaDefaultBaseUrlPrefix;
		this.setCampaignActionPrefix = setCampaignActionConfig || scaDefaultBaseUrlPrefix;

		// bind getAuthToken function to avoid losing context after passing the function as param
		this.getAuthToken = this.getAuthToken.bind(this);
	}

	public async getContent(
		requestedContent: ContentType): Promise<ContentResponse> {
		if (Caching.isCachedTypeExpired(ContentTypeToStorageKeyMap[requestedContent])) {
			await this.refreshItemsInCacheFromTmsService([requestedContent]);
		}

		const response: ContentResponse = this.retrieveCacheItemAndCreateContentResponse(requestedContent);
		return response;
	}

	public async getMultipleContents(
		requestedContent: ContentType[]): Promise<ContentResponse[]> {
		await this.refreshItemsInCacheFromTmsService(requestedContent);

		const contentResponses: ContentResponse[] = new Array<ContentResponse>();
		const bulkRefreshList: ContentType[] = new Array<ContentType>();
		for (const contentType of requestedContent) {
			const responseItem = this.createCachedContentResponse(contentType);
			contentResponses.push(responseItem);
		}

		this.refreshItemsInCacheFromTmsService(bulkRefreshList);

		return contentResponses;
	}

	public async getUserFactsContent(
		requestedContent: ContentType): Promise<ContentResponse> {
		await this.refreshItemsInCacheFromSubstrateTulipsService(requestedContent);
		const response: ContentResponse = this.retrieveCacheItemAndCreateContentResponse(requestedContent);
		return response;
	}

	public async sendBeaconRequestToService(
		app: string,
		beaconParameters: IBeaconParameters,
		platform: string,
		version: string,
		locale: string,
		country: string,
		sessionId: string,
		authTokenCallback: IFloodgateAuthTokenCallback,
		): Promise<boolean> {
		this.sessionId = sessionId;
		this.authTokenCallback = authTokenCallback;

		const authTokenConfig: IAuthTokenConfig = {
			scenario: 'TMS',
			appId: getDynamicSetting(SettingKey.tulipsAppId, TmsgConstants.GUIDs.TMSAppID)
		};

		const authToken = await this.getAuthToken(authTokenConfig);
		let puid: string;
		if (!authToken) {
			puid = this.authTokenCallback.getUserPuid && await this.authTokenCallback.getUserPuid();
		}

		const requestStartTime = +new Date();
		try {
			const tmsQueryParams: IQueryParams = {
				app,
				country,
				locale,
				platform,
				puid,
				version,
			};

			const requestBody = JSON.stringify({
				actionName: beaconParameters.actionName,
				actionUrl: beaconParameters.actionUrl,
			});

			const header: Record<string, string> = {
				"Content-Type": "application/json;charset=utf-8",
			};

			const response: Response = await this.sendRequest(
				tmsQueryParams,
				authToken,
				SettingKey.tmsActionUrl,
				this.setCampaignActionPrefix + TmsgConstants.TMS_SETCAMPAIGNACTION_ENDPOINT + "?",
				header,
				requestBody,
				"POST");

			if (!response || response.status !== 200) {
				Logging.getLogger().event(
					EventIds.SetCampaignActionError,
					this.createTelemetryData(requestStartTime, response, `Beacon${beaconParameters.actionName}`) as Logging.ICustomLogData);
				return false;
			}
			Logging.getLogger().info(
				EventIds.SetCampaignActionRequests,
				this.createTelemetryData(requestStartTime, response, `Beacon${beaconParameters.actionName}`), .01);
			return true;
		} catch (error) {
			Logging.getLogger().event(
				EventIds.SetCampaignActionError,
				{
					ErrorMessage:
						"Error fetching from TMS SetCampaignAction endpoint:" + getErrorMessage(error),
					...this.createTelemetryData(requestStartTime, null, `Beacon${beaconParameters.actionName}`),
				});
			return false;
		}
	}

	public async getTulipsServiceUserFactsContent(): Promise<IUserFact[]> {
		return getUserFactList(null, this.getAuthToken);
	}

	private parseCampaignIdFromResponse(response: ICampaignDefinition[]): string {
		const campaignIds: string[] = response?.map((campaign: any) => campaign.CampaignId);
		return campaignIds?.toString();
	}

	private createTelemetryData(requestStartTimestamp: number, response: Response, type: string, campaignIds?: string): ICustomProperties {
		const { headers, status, statusText } = response || {} as Response;
		const serverSessionId = headers && headers.get("X-UserSessionId");
		const serverCorrelationId = headers && headers.get("X-CorrelationId");
		return {
			Type: type,
			TimeMilliseconds: +new Date() - requestStartTimestamp,
			SessionId: serverSessionId || this.sessionId,
			CorrelationId: serverCorrelationId || undefined,
			HttpStatusCode: status || undefined,
			HttpStatusText: statusText || undefined,

			Flights: this.campaignFlights,
			CampaignId: campaignIds || undefined,
		};
	}

	private sendRequest(
		tmsgQueryParams: IQueryParams,
		authToken: string,
		key: SettingKey,
		defaultBaseUrl: string,
		requestHeaders?: Record<string, string>,
		requestBody?: string,
		verb?: string): Promise<Response> {
		const queryParamStr = buildQueryParameters(tmsgQueryParams);
		const tmsgBaseUrl = getDynamicSetting(key, defaultBaseUrl);
		const requestPromise = verb === "POST" ?
			Transport.sendPostRequest(tmsgBaseUrl + queryParamStr, requestBody, authToken, requestHeaders, this.sessionId) :
			Transport.sendGetRequest(tmsgBaseUrl + queryParamStr, authToken, this.sessionId);
		return requestPromise;
	}

	private retrieveCacheItemAndCreateContentResponse(contentType: ContentType): ContentResponse {
		const responseItem: ContentResponse = this.createCachedContentResponse(contentType);
		if (Caching.isCachedTypeExpired(ContentTypeToStorageKeyMap[contentType])) {
			if (contentType === ContentType.userFacts) {
				this.refreshItemsInCacheFromSubstrateTulipsService(contentType);
			} else {
				this.refreshItemsInCacheFromTmsService([contentType]);
			}
		}
		else { // cache is not expired
			if (contentType === ContentType.campaignContent) {
				Logging.getLogger().event(
					EventIds.CampaignRetrievedCache, { campaignIds: this.parseCampaignIdFromResponse(responseItem?.content?.campaigns) }, getSamplingRate());
			}
		}
		return responseItem;
	}

	private createCachedContentResponse(requestedContent: ContentType): ContentResponse {
		const contentResponse: ContentResponse = new ContentResponse();
		contentResponse.content = Caching.getCachedContent(ContentTypeToStorageKeyMap[requestedContent]);
		contentResponse.contentType = requestedContent;

		return contentResponse;
	}

	private async getAuthToken(authTokenConfig: IAuthTokenConfig): Promise<string> {
		if (!this.authTokenCallback || !this.authTokenCallback.getAuthToken || !authTokenConfig) {
			return null;
		}

		const startTime = +new Date();
		const getDuration = () => +new Date() - startTime;
		const samplingRate = getSamplingRate();

		const createAuthTelemetryLogData = (message: string, tokenLength = -1): Logging.ICustomLogData => {
			return {
				Message: message,
				SamplingRate: samplingRate,
				Scenario: authTokenConfig.scenario,
				SessionId: this.sessionId,
				TimeMilliseconds: getDuration(),
				TokenLegth: tokenLength,
				Type: authTokenConfig.appId,
			};
		}

		try {
			const retVal = await this.authTokenCallback.getAuthToken(authTokenConfig);
			Logging.getLogger().event(
				EventIds.EngagementAuthSuccessful,
				createAuthTelemetryLogData("getAuthToken Successful", retVal?.length), samplingRate);

			return retVal;
		} catch (error) {
			Logging.getLogger().event(
				EventIds.EngagementAuthError,
				createAuthTelemetryLogData("Error getting token:" + getErrorMessage(error))
			);
		}

		return null;
	}

	private async refreshItemsInCacheFromTmsService(contentTypes: ContentType[]): Promise<void> {
		if (contentTypes.length === 0) {
			return;
		}

		// check if touched items are actually due for refresh or are still valid
		const bulkRefreshList: ContentType[] = new Array<ContentType>();
		for (const contentType of contentTypes) {
			if (Caching.isCachedTypeExpired(ContentTypeToStorageKeyMap[contentType])) {
				bulkRefreshList.push(contentType);
			}
		}

		if (bulkRefreshList.length === 0) {
			// exit out if all items in cache are still valid to avoid unnecessary service roundtrips
			return;
		}

		// always request for dynamic settings
		if (contentTypes.indexOf(ContentType.dynamicSettings) === -1) {
			contentTypes.push(ContentType.dynamicSettings);
		}

		// get auth token or user id
		const authTokenConfig: IAuthTokenConfig = {
			scenario: 'TMS',
			appId: getDynamicSetting(SettingKey.tulipsAppId, TmsgConstants.GUIDs.TMSAppID)
		};

		const authToken = await this.getAuthToken(authTokenConfig);
		let userPuid: string;
		if (!authToken) {
			userPuid = this.authTokenCallback.getUserPuid && await this.authTokenCallback.getUserPuid();
		}

		const requestStartTime = +new Date();

		// make service call for expired or uncached items
		try {
			// capture browser data to perform additional filters on IRIS
			// this is to avoid showing bad UI experience on unsupported browsers/dimensions
			const documentElement = document && (document.documentElement || document.getElementsByTagName("body")[0]);
			const browserParams: IQueryParams = {
				pageWidth: window.innerWidth || documentElement.clientWidth,
				pageHeight: window.innerHeight || documentElement.clientHeight,
				screenWidth: screen ? screen.width : undefined,
				screenHeight: screen ? screen.height : undefined,
				colorDepth: screen ? screen.colorDepth : undefined,
			};

			// allow testing with query param overrides with key obf-DebugOverrides
			// eg: {"addTmsQueryParams":{"key1":"key1value"},"replaceTmsQueryParams":{"key2":"key2value"}, "tmsResponse": {}}
			const debugOverrides = Storage.readObjectFromStorage<IDebugOverrides>("obf-DebugOverrides", {});
			const tmsQueryParams: IQueryParams = debugOverrides.replaceTmsQueryParams || {
				country: this.country,
				locale: this.locale,
				app: this.app,
				platform: this.platform,
				version: this.version,
				campaignParams: buildQueryParameters(browserParams) + "&more=true&" + (this.campaignParameters || ""),
				contentType: contentTypes.join(";"),
				puid: userPuid,
				OFC_FLIGHTS: this.campaignFlights,
				...(debugOverrides.addTmsQueryParams || {}),
				ageGroup: this.ageGroup,
				sessionUserType: this.authType,
				packageVersion: this.packageVersion,
			};

			const response: Response = await this.sendRequest(
				tmsQueryParams,
				authToken,
				SettingKey.tmsServiceUrl,
				this.campaignMetadataAggregatorPrefix + TmsgConstants.TMS_CAMPAIGNMETADATAAGGREGATOR_ENDPOINT + "?");
			if (response.status === 200) {
				const tmsServiceResponse: ITmsServiceResponse = await response.json();

				if (contentTypes.indexOf(ContentType.messageMetadata) !== -1
					&& tmsServiceResponse.MessageMetadata !== undefined) {
					Caching.saveToCache(
						ContentType.messageMetadata,
						tmsServiceResponse.MessageMetadata,
						getDynamicSetting(SettingKey.tmsMessageMetadataExpiry, TmsgConstants.CACHE_EXPIRY_MESSAGEMETADATA),
						ContentTypeToStorageKeyMap[ContentType.messageMetadata]);
				}

				if (contentTypes.indexOf(ContentType.userGovernanceRules) !== -1
					&& tmsServiceResponse.UserGovernanceRules !== undefined) {
					Caching.saveToCache(
						ContentType.userGovernanceRules,
						tmsServiceResponse.UserGovernanceRules,
						getDynamicSetting(SettingKey.tmsUserGovernanceRulesExpiry, TmsgConstants.CACHE_EXPIRY_USERGOVERNANCERULES),
						ContentTypeToStorageKeyMap[ContentType.userGovernanceRules]);
				}

				if (contentTypes.indexOf(ContentType.campaignContent) !== -1
					&& tmsServiceResponse.CampaignContent !== undefined) {
					Caching.saveToCache(
						ContentType.campaignContent,
						tmsServiceResponse.CampaignContent,
						getDynamicSetting(SettingKey.tmsCampaignContentExpiry, TmsgConstants.CACHE_EXPIRY_CAMPAIGNCONTENT),
						ContentTypeToStorageKeyMap[ContentType.campaignContent]);
				}

				// There is no explicit request for the next set of ContentTypes in the code
				// If the server responds with these types save and use them later
				if (tmsServiceResponse.DynamicSettings !== undefined) {
					Caching.saveToCache(
						ContentType.dynamicSettings,
						tmsServiceResponse.DynamicSettings,
						getDynamicSetting(SettingKey.tmsDynamicSettingsExpiry, TmsgConstants.CACHE_EXPIRY_DYNAMICSETTINGS),
						ContentTypeToStorageKeyMap[ContentType.dynamicSettings]);
				}

				if (tmsServiceResponse.LogLevelSettings !== undefined) {
					Caching.saveToCache(
						ContentType.logLevelSettings,
						tmsServiceResponse.LogLevelSettings,
						getDynamicSetting(SettingKey.tmsLogLevelSettingsExpiry, TmsgConstants.CACHE_EXPIRY_LOGLEVELSETTINGS),
						ContentTypeToStorageKeyMap[ContentType.logLevelSettings]);
				}

				Logging.getLogger().event(
					EventIds.CampaignMetadataAggregatorRequests,
					this.createTelemetryData(requestStartTime, response, contentTypes.join(";"), 
						this.parseCampaignIdFromResponse(tmsServiceResponse?.CampaignContent?.campaigns)) as Logging.ICustomLogData,
					0.01);
			} else {
				Logging.getLogger().event(
					EventIds.CampaignMetadataAggregatorError,
					this.createTelemetryData(requestStartTime, response, contentTypes.join(";")) as Logging.ICustomLogData);
			}
		} catch (error) {
			Logging.getLogger().event(
				EventIds.CampaignMetadataAggregatorError,
				{
					ErrorMessage: "Error fetching from TMS:" + getErrorMessage(error),
					...this.createTelemetryData(requestStartTime, null, contentTypes.join(";")),
				});
		}
	}

	private async refreshItemsInCacheFromSubstrateTulipsService(contentType: ContentType): Promise<void> {
		// check if touched item is actually due for refresh or is still valid
		if (!Caching.isCachedTypeExpired(ContentTypeToStorageKeyMap[contentType])) {
			// exit out if the cache is still valid to avoid unnecessary service roundtrips
			return;
		}

		const authTokenConfig: IAuthTokenConfig = {
			scenario: 'TulipsSubstrate',
			appId: getDynamicSetting(SettingKey.tulipsAppId, TmsgConstants.GUIDs.TULIPSSubstrateAppID)
		};

		const authToken = await this.getAuthToken(authTokenConfig);
		if (!authToken) {
			// if authToken is null then exit out since service call cannot be made without authToken
			return;
		}

		const requestStartTime = +new Date();

		// make service call for expired or uncached item
		try {
			const response: Response = await this.sendRequest(
				{},
				authToken,
				SettingKey.tulipsServiceUrl,
				TmsgConstants.TULIPS_SERVICEURL);
			if (response.status === 200 || response.status === 204) {
				const tulipsServiceResponse = await response.json();

				if (contentType.indexOf(ContentType.userFacts) !== -1
					&& tulipsServiceResponse !== undefined) {
					const normalizedUserFacts = addClientIngestionTimeAndNormalizeKeys(tulipsServiceResponse);
					Caching.saveToCache(
							ContentType.userFacts,
							normalizedUserFacts,
							TmsgConstants.CACHE_EXPIRY_USERFACTS,
							ContentTypeToStorageKeyMap[ContentType.userFacts]);
				}

				Logging.getLogger().event(
					EventIds.UserFactsMessagingRequests,
					this.createTelemetryData(requestStartTime, response, contentType) as Logging.ICustomLogData, .01);
			} else {
				Logging.getLogger().event(
					EventIds.UserFactsCommonError,
					this.createTelemetryData(requestStartTime, response, contentType) as Logging.ICustomLogData);
			}
		} catch (error) {
			Logging.getLogger().event(
				EventIds.UserFactsCommonError,
				{
					ErrorMessage: "Error fetching from TULIPS:" + getErrorMessage(error),
					...this.createTelemetryData(requestStartTime, null, contentType),
				});
		}
	}
}
